/**
 * DI-2-4 — what the browser read, as the `inspection-3/v1` 2.1.0 wire object.
 *
 * One `DataPointInput` per index row (story §2.4):
 *
 *   externalId          uuidv5(INTAKE_NS, dataAcquisitionId + ':' + payloadSha256Hex)
 *   imageFilename       the basename; the second, third … of a same-name-
 *                       different-payload group (DI-2-2) go on the wire as
 *                       `<stem>~<n>.<ext>` and keep the original in
 *                       `exif.OriginalFileName` (the server keys its image row
 *                       by filename — a second image under one name is a 422)
 *   capturedAtMs        `normalized.capturedAtMs` — EXIF read as UTC, raw
 *                       (DI-D20 c1); `OffsetTimeOriginal` travels in `exif`
 *   gps                 { lat, lon, altRelative } — RELATIVE only (DI-D19);
 *                       `gps.alt` is gone (DI-D34 c3) and never sent
 *   droneOrientation /  the six XMP degree keys
 *   gimbalOrientation
 *   image               the camera / file facts (absent tag → key omitted)
 *   exif                the STORED shape: EXIF tags flat by original name
 *                       (rationals `[n, d]`, sub-seconds strings), plus one
 *                       object per XMP namespace prefix — `"drone-dji": {…}`
 *                       holds the drone-dji keys by local name (13 on the Sony,
 *                       32 on the P1); every other XMP property sits under its
 *                       own prefix. That nesting is what keeps two namespaces
 *                       from colliding on `Version`.
 *
 * NOT sent: `componentId` (the browser does not know the blade before the
 * geometry stage, DI-2-8), `componentCoordinate`, the deprecated `metadata`,
 * and anything derived (DI-D20: no corrected time from the client).
 *
 * Pure except for the SHA-1 of the UUIDv5 (WebCrypto, async).
 */
import type { Inspection3Components } from "@aeriosiq/types";
import type { ExifValue, ParsedImage } from "../parse/types.js";
import { dataPointExternalId } from "./externalId.js";

export type DataPointInput = Inspection3Components["schemas"]["DataPointInput"];
export type ImageMetadata = Inspection3Components["schemas"]["ImageMetadata"];

/** The minimum a row needs to be sent (an `IndexRow` that is parsed and hashed). */
export interface SyncRow {
  index: number;
  name: string;
  relativePath: string;
  size: number;
  parsed: ParsedImage;
  hashes: { md5Hex: string; payloadSha256Hex: string | null };
}

/** The XMP namespace prefix the drone-dji block is stored under. */
export const DRONE_DJI = "drone-dji";

/**
 * The stored `exif` object (story §2.4, a shape choice for Christian):
 * EXIF flat by original tag name, one object per XMP prefix.
 */
export function storedExif(
  parsed: Pick<ParsedImage, "exif" | "xmp" | "xmpOther">,
  originalFileName?: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...parsed.exif };
  if (Object.keys(parsed.xmp).length > 0) out[DRONE_DJI] = { ...parsed.xmp };
  for (const [qualified, value] of Object.entries(parsed.xmpOther)) {
    const colon = qualified.indexOf(":");
    if (colon <= 0) continue;
    const prefix = qualified.slice(0, colon);
    const local = qualified.slice(colon + 1);
    // An EXIF tag of the same name wins its top-level slot; the namespace is
    // then dropped rather than merged into a scalar (never observed: EXIF
    // names are CamelCase, XMP prefixes are lower-case).
    const slot = out[prefix];
    if (
      slot !== undefined &&
      (typeof slot !== "object" || slot === null || Array.isArray(slot))
    )
      continue;
    const group = (slot ?? {}) as Record<string, string>;
    group[local] = value;
    out[prefix] = group;
  }
  if (originalFileName !== undefined) out.OriginalFileName = originalFileName;
  return out;
}

function str(v: ExifValue | undefined): string | undefined {
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : undefined;
}

/** Set `key` only when the value exists — a missing tag is omitted, never `null`. */
function put<K extends keyof ImageMetadata>(
  o: ImageMetadata,
  key: K,
  v: NonNullable<ImageMetadata[K]> | undefined,
): void {
  if (v !== undefined) o[key] = v;
}

export function imageMetadataOf(row: SyncRow): ImageMetadata {
  const n = row.parsed.normalized;
  const exif = row.parsed.exif;
  const xmp = row.parsed.xmp;
  const img: ImageMetadata = {};
  put(img, "make", n.make);
  put(img, "model", n.model);
  put(img, "lensModel", str(exif.LensModel));
  put(img, "software", n.software);
  put(img, "focalLengthMm", n.focalLengthMm);
  put(img, "fNumber", n.fNumber);
  put(img, "iso", n.iso !== undefined ? Math.round(n.iso) : undefined);
  put(img, "exposureTime", n.exposureTime);
  put(img, "widthPx", n.width);
  put(img, "heightPx", n.height);
  const o = exif.Orientation;
  put(
    img,
    "orientation",
    typeof o === "number" && Number.isInteger(o) && o >= 1 && o <= 8
      ? o
      : undefined,
  );
  put(
    img,
    "cameraSerial",
    str(xmp.CameraSerialNumber) ?? str(exif.BodySerialNumber),
  );
  put(img, "droneSerial", str(xmp.DroneSerialNumber));
  // DI-D19 — the ABSOLUTE altitude: EXIF GPSAltitude, the XMP AbsoluteAltitude where EXIF has none.
  put(img, "gpsAltitudeAbs", n.altAbs ?? n.altAbsXmp);
  put(img, "payloadHash", row.hashes.payloadSha256Hex ?? undefined);
  put(img, "fileHashMd5", row.hashes.md5Hex);
  put(img, "sizeBytes", row.size);
  return img;
}

/**
 * The wire object for one row. `wireName` is the renamed filename of a
 * same-name twin (see `wireNames`); the original name then rides in `exif`.
 */
export async function toDataPointInput(
  row: SyncRow,
  dataAcquisitionId: string,
  wireName?: string,
): Promise<DataPointInput> {
  const n = row.parsed.normalized;
  const renamed = wireName !== undefined && wireName !== row.name;
  const dp: DataPointInput = {
    externalId: await dataPointExternalId(dataAcquisitionId, row.hashes),
    imageFilename: renamed ? wireName : row.name,
  };
  if (n.capturedAtMs !== undefined) dp.capturedAtMs = n.capturedAtMs;
  if (n.lat !== undefined && n.lon !== undefined) {
    dp.gps = {
      lat: n.lat,
      lon: n.lon,
      ...(n.altRel !== undefined ? { altRelative: n.altRel } : {}),
    };
  }
  if (n.flight) dp.droneOrientation = { ...n.flight };
  if (n.gimbal) dp.gimbalOrientation = { ...n.gimbal };
  dp.image = imageMetadataOf(row);
  dp.exif = storedExif(row.parsed, renamed ? row.name : undefined);
  return dp;
}

/**
 * The wire filename of every row of ONE acquisition. Rows are numbered per
 * basename in relative-path order (code-point order, then row index — the
 * DI-2-2 rule), so a re-drop of the same folder names every row the same way:
 * the first keeps its name, the n-th becomes `<stem>~<n>.<ext>`.
 */
export function wireNames(
  rows: readonly Pick<SyncRow, "index" | "name" | "relativePath">[],
): Map<number, string> {
  const byName = new Map<
    string,
    Pick<SyncRow, "index" | "name" | "relativePath">[]
  >();
  for (const r of rows) {
    const g = byName.get(r.name);
    if (g) g.push(r);
    else byName.set(r.name, [r]);
  }
  const out = new Map<number, string>();
  for (const [name, group] of byName) {
    const sorted = [...group].sort((a, b) =>
      a.relativePath < b.relativePath
        ? -1
        : a.relativePath > b.relativePath
          ? 1
          : a.index - b.index,
    );
    const dot = name.lastIndexOf(".");
    const stem = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : "";
    sorted.forEach((r, i) => {
      out.set(r.index, i === 0 ? name : `${stem}~${String(i + 1)}${ext}`);
    });
  }
  return out;
}

/** Contract max is 5 000; the story sends 500 per request. */
export const BATCH_MAX_ITEMS = 500;
/**
 * The gateway's JSON body limit is Fastify's default, 1 MiB (measured: 500
 * Sony items are ≈ 720 kB). A P1 row carries ~2.5× the XMP, so a batch is also
 * closed before its serialised size passes this bound.
 */
export const BATCH_MAX_BYTES = 900_000;

export function toBatches<T>(
  items: readonly T[],
  maxItems: number = BATCH_MAX_ITEMS,
  maxBytes: number = BATCH_MAX_BYTES,
): T[][] {
  const out: T[][] = [];
  let cur: T[] = [];
  let bytes = 0;
  for (const it of items) {
    const size = JSON.stringify(it).length + 1;
    if (cur.length > 0 && (cur.length >= maxItems || bytes + size > maxBytes)) {
      out.push(cur);
      cur = [];
      bytes = 0;
    }
    cur.push(it);
    bytes += size;
  }
  if (cur.length > 0) out.push(cur);
  return out;
}
